/**
 * Hook for managing Gmail OAuth2 connection for DTE reception.
 */

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTenantStore } from "@/stores/tenant-store";
import { apiFetch } from "@/lib/api-client";

interface GmailConnectionStatus {
  connected: boolean;
  email: string | null;
  provider: string | null;
}

export function useGmailConnection() {
  const activeTenantId = useTenantStore((state) => state.activeTenantId);
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<GmailConnectionStatus>({
    connected: false,
    email: null,
    provider: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Check for OAuth callback results in URL
  useEffect(() => {
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");
    const email = searchParams.get("email");

    if (oauthSuccess === "true") {
      // Clear URL params
      searchParams.delete("oauth_success");
      searchParams.delete("email");
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });

      // Update status with connected email
      if (email) {
        setStatus({
          connected: true,
          email: decodeURIComponent(email),
          provider: "google",
        });
      }
    } else if (oauthError) {
      // Clear URL params
      searchParams.delete("oauth_error");
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });

      // The component will show an error toast based on this
    }
  }, [searchParams, setSearchParams]);

  // Fetch current connection status
  const fetchStatus = useCallback(async () => {
    if (!activeTenantId) return;

    setIsLoading(true);
    try {
      const data = await apiFetch.get<GmailConnectionStatus>("/api/oauth2/google/status");
      setStatus(data);
    } catch (error) {
      console.error("Failed to fetch Gmail connection status:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeTenantId]);

  // Fetch status on mount and tenant change
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Initiate OAuth2 flow
  const connect = useCallback(
    async (email: string) => {
      if (!activeTenantId) {
        throw new Error("No tenant selected");
      }

      setIsConnecting(true);
      try {
        const { authUrl } = await apiFetch.get<{ authUrl: string }>(
          `/api/oauth2/google/authorize?email=${encodeURIComponent(email)}`
        );

        // Redirect to Google consent screen
        window.location.href = authUrl;
      } catch (error) {
        setIsConnecting(false);
        throw error;
      }
    },
    [activeTenantId]
  );

  // Disconnect Gmail
  const disconnect = useCallback(async () => {
    if (!activeTenantId) {
      throw new Error("No tenant selected");
    }

    setIsDisconnecting(true);
    try {
      await apiFetch.get<{ success: boolean }>("/api/oauth2/google/disconnect");

      setStatus({
        connected: false,
        email: null,
        provider: null,
      });
    } finally {
      setIsDisconnecting(false);
    }
  }, [activeTenantId]);

  // Get OAuth error from URL (if any)
  const oauthError = searchParams.get("oauth_error");

  return {
    status,
    isLoading,
    isConnecting,
    isDisconnecting,
    connect,
    disconnect,
    refetch: fetchStatus,
    oauthError,
  };
}
